/**
 * What has happened to this register's shape.
 *
 * The trail has always been recorded. It could not be read anywhere in the product, which
 * meant "how is this audited?" had the answer "it is, but you would need a database". That
 * is not an audit trail — it is a table that would become one if somebody with SQL access
 * happened to be free at the moment the question was asked.
 *
 * The question is asked by auditors, by clients disputing a figure, and by administrators
 * working out how a register ended up in its current shape. So it sits directly under the
 * fields it describes, on the screen where the changes are made.
 *
 * Each entry says who, when, and what moved. Where a label changed, both labels are shown —
 * a trail that records only the new value answers "what is it now", which is the one thing
 * the reader can already see.
 */
import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { api } from '@/api/client'
import { Empty, Failed, Skeleton } from '@/components/states'

type AuditEntry = {
  id: string
  at: string
  actor_id: string | null
  action: string
  entity_table: string
  entity_id: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  via_ai: boolean
}

/**
 * The field properties worth showing a reader, and the string each is named by.
 *
 * Not the whole row — see the API's `_AUDITED` — and not the raw column names either. The
 * person reading this is establishing what a register was allowed to record on a given
 * date; `workbook_column` is something they would have to decode first.
 */
const NOTABLE: [name: string, label: string][] = [
  ['label_en', 'builder.label'],
  ['label_ar', 'builder.labelAr'],
  ['key', 'builder.key'],
  ['kind', 'builder.kind'],
  ['required', 'builder.required'],
  ['in_grid', 'builder.inGrid'],
  ['section', 'builder.section'],
  ['workbook_column', 'builder.workbook'],
  ['status', 'builder.status'],
  ['retire_requested', 'builder.retire'],
]

/**
 * One side of a change, as a reader should see it.
 *
 * `name` is passed so the few properties with real names elsewhere in the product keep them
 * here. An audit entry is the last place to hand somebody `text` and `published` and let
 * them map it back themselves.
 */
function show(value: unknown, name?: string, t?: TFunction): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return t ? t(value ? 'value.yes' : 'value.no') : String(value)
  if (typeof value === 'object') return JSON.stringify(value)
  if (t && name === 'kind') return t(`builder.kinds.${value}`, String(value))
  if (t && name === 'status') return t(`builder.statuses.${value}`, String(value))
  return String(value)
}

/** The properties that actually moved between the two sides. */
function changes(
  entry: AuditEntry,
): { name: string; label: string; from: unknown; to: unknown }[] {
  const before = entry.before ?? {}
  const after = entry.after ?? {}
  return NOTABLE.filter(([name]) => show(before[name]) !== show(after[name])).map(
    ([name, label]) => ({ name, label, from: before[name], to: after[name] }),
  )
}

export function FieldHistory({ slug }: { slug: string }) {
  const { t, i18n } = useTranslation()

  const trail = useQuery({
    queryKey: ['audit', slug],
    queryFn: ({ signal }) =>
      api.get<{ items: AuditEntry[] }>(
        `/audit?entity_table=${encodeURIComponent(slug)}&limit=50`,
        signal,
      ),
  })

  const when = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  return (
    <section className="card card--pad" data-testid="builder-history">
      <h2 className="card__title">{t('builder.history')}</h2>
      <p className="card__caption card__caption--note">{t('builder.historyNote')}</p>

      {trail.isPending ? (
        <Skeleton rows={3} />
      ) : trail.isError ? (
        <Failed message={t('builder.historyFailed')} onRetry={() => void trail.refetch()} />
      ) : (trail.data?.items ?? []).length === 0 ? (
        <Empty title={t('builder.noHistory')} />
      ) : (
        <ol className="trail">
          {(trail.data?.items ?? []).map((entry) => {
            const moved = changes(entry)
            const name =
              (entry.after?.['label_en'] as string | undefined) ??
              (entry.before?.['label_en'] as string | undefined) ??
              ''
            return (
              <li key={entry.id} className="trail__entry" data-testid="trail-entry">
                <div className="trail__head">
                  <span className="trail__action" data-action={entry.action}>
                    {t(`builder.actions.${entry.action}`, entry.action)}
                  </span>
                  {name ? <span className="trail__subject">{name}</span> : null}
                  {/* The copilot acts *as* a person, so "who" and "was a human reading"
                      are different questions. A client disputing a decision asks the
                      second one, and it has to be answerable from the entry itself. */}
                  {entry.via_ai ? (
                    <span className="chip chip--info" data-testid="trail-ai">
                      {t('builder.viaAi')}
                    </span>
                  ) : null}
                  <time className="trail__when" dateTime={entry.at}>
                    {when.format(new Date(entry.at))}
                  </time>
                </div>

                {moved.length > 0 ? (
                  <ul className="trail__changes">
                    {moved.map((change) => (
                      <li key={change.name} className="trail__change">
                        <span className="trail__field">{t(change.label)}</span>
                        {/* Both sides. A trail that records only the new value answers
                            "what is it now", which the reader can already see. */}
                        <s className="trail__from">{show(change.from, change.name, t)}</s>
                        <span className="trail__to">{show(change.to, change.name, t)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
